/* ============================================================
   Dathent — Public referral signup (/r/:code)
   Lead capture, NO password. Stores referral_signups row.
   Source: design/Referral Signup.html
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import ThemeToggle from '../components/AppShell/ThemeToggle.jsx';

const COPY = {
  en: {
    have: 'Already have an account?',
    signIn: 'Sign in',
    eyebrow: 'Closed beta · referral access',
    headPre: 'thinks Dathent would',
    headAccent: 'work for you.',
    formTitle: 'Reserve your spot',
    formSub: "Takes ~30 seconds. We'll let you in when we open public access.",
    emailL: 'Email',
    emailPh: 'you@yourstudio.com',
    contactL: 'Phone or telegram',
    contactOpt: 'Optional · for launch notification',
    contactPh: '+49 ··· or @handle',
    contactKindPhone: 'Phone',
    contactKindTelegram: 'Telegram',
    contactKindEmail: 'Other email',
    contactKindOther: 'Other',
    cta: 'Reserve my spot',
    submitting: 'Sending…',
    foot: ['By creating an account you agree to our ', 'terms', ' and ', 'privacy policy', '.'],
    bonus: '+ 2 months free',
    perks: [
      { l: 'Referral bonus · 2 months free on activation', s: 'Locked to your account and auto-applied when we open the door.' },
      { l: 'A spot reserved before public launch', s: "We're in closed beta. You won't get access today — but you skip the public waitlist." },
      { l: 'A heads-up the day we go public', s: "Optional. We'll only ping you about the launch, nothing else." },
    ],
    notFound: 'Referral link not found',
    errorGeneric: 'Could not submit, please try again',
  },
  uk: {
    have: 'Вже маєш аккаунт?',
    signIn: 'Увійти',
    eyebrow: 'Closed beta · реферальний доступ',
    headPre: 'вважає, що Dathent',
    headAccent: 'тобі підійде.',
    formTitle: 'Зарезервуй місце',
    formSub: 'Це ~30 секунд. Запрошуємо як тільки відкриємо публічний доступ.',
    emailL: 'Email',
    emailPh: 'you@yourstudio.com',
    contactL: 'Телефон або telegram',
    contactOpt: 'Опціонально · щоб повідомити про запуск',
    contactPh: '+380 ··· або @handle',
    contactKindPhone: 'Телефон',
    contactKindTelegram: 'Telegram',
    contactKindEmail: 'Інший email',
    contactKindOther: 'Інше',
    cta: 'Зарезервувати місце',
    submitting: 'Надсилаю…',
    foot: ['Реєструючись, ти погоджуєшся з нашими ', 'умовами', ' та ', 'політикою приватності', '.'],
    bonus: '+ 2 місяці в подарунок',
    perks: [
      { l: 'Бонус від запрошувача · 2 місяці в подарунок при підключенні', s: 'Прив\'язано до твого акаунту, активується автоматично при відкритті.' },
      { l: 'Місце зарезервовано до публічного запуску', s: 'Зараз closed beta. Доступу сьогодні не буде — але ти пропускаєш загальний waitlist.' },
      { l: 'Повідомимо в день публічного запуску', s: 'Опціонально. Напишемо тільки про запуск, нічого більше.' },
    ],
    notFound: 'Реферальне посилання не знайдено',
    errorGeneric: 'Не вдалося зберегти, спробуй ще раз',
  },
  ru: {
    have: 'Уже есть аккаунт?',
    signIn: 'Войти',
    eyebrow: 'Closed beta · реферальный доступ',
    headPre: 'считает, что Dathent',
    headAccent: 'тебе подойдёт.',
    formTitle: 'Зарезервируй место',
    formSub: 'Около 30 секунд. Откроем доступ — сразу пригласим.',
    emailL: 'Email',
    emailPh: 'you@yourstudio.com',
    contactL: 'Телефон или telegram',
    contactOpt: 'Опционально · уведомить о запуске',
    contactPh: '+1 ··· или @handle',
    contactKindPhone: 'Телефон',
    contactKindTelegram: 'Telegram',
    contactKindEmail: 'Другой email',
    contactKindOther: 'Другое',
    cta: 'Зарезервировать место',
    submitting: 'Отправляю…',
    foot: ['Регистрируясь, вы соглашаетесь с нашими ', 'условиями', ' и ', 'политикой приватности', '.'],
    bonus: '+ 2 месяца в подарок',
    perks: [
      { l: 'Бонус от пригласившего · 2 месяца в подарок при подключении', s: 'Закреплено за вашим аккаунтом, применяется автоматически.' },
      { l: 'Место зарезервировано до публичного запуска', s: 'Сейчас closed beta. Сегодня доступа нет — но вы вне общей очереди.' },
      { l: 'Сообщим в день публичного запуска', s: 'Опционально. Только о запуске, ничего больше.' },
    ],
    notFound: 'Реферальная ссылка не найдена',
    errorGeneric: 'Не удалось сохранить, попробуйте ещё раз',
  },
};

function detectLocale() {
  if (typeof navigator === 'undefined') return 'en';
  const lang = (navigator.language || 'en').slice(0, 2).toLowerCase();
  if (['uk', 'ru', 'en'].includes(lang)) return lang;
  return 'en';
}

const DEFAULT_GRADIENT = ['#D9CBB3', '#766858'];

export function ReferralSignup() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [locale, setLocale] = useState(detectLocale());
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [email, setEmail] = useState('');
  const [contactKind, setContactKind] = useState('phone');
  const [contactValue, setContactValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const t = COPY[locale] || COPY.en;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const result = await api.referral.lookupByCode(code);
        if (cancelled) return;
        if (result?.referrer) {
          setReferrer(result.referrer);
          sessionStorage.setItem('dathent.referralCode', code);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        if (err?.status === 404) setNotFound(true);
        else setError(t.errorGeneric);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const utm = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const out = {};
    for (const [k, v] of params.entries()) {
      if (k.startsWith('utm_')) out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.includes('@')) {
      setError(t.emailL + ' invalid');
      return;
    }
    setSubmitting(true);
    try {
      await api.referral.submitSignup({
        code,
        email: email.trim().toLowerCase(),
        contactKind: contactValue.trim() ? contactKind : null,
        contactValue: contactValue.trim() || null,
        locale,
        utm,
      });
      sessionStorage.setItem('dathent.referralSignup', JSON.stringify({
        code,
        email,
        contactKind,
        contactValue,
        referrer,
        ts: Date.now(),
      }));
      navigate(`/r/${encodeURIComponent(code)}/welcome`, { replace: true });
    } catch (err) {
      setError(err?.message || t.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rs-page">
        <div style={{ padding: '120px 0', textAlign: 'center', color: 'var(--text-muted)' }}>…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="rs-page">
        <div style={{ padding: '120px 24px', textAlign: 'center' }}>
          <h1 className="rs-h" style={{ fontSize: 32 }}>{t.notFound}</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>
            <Link to="/">← Dathent</Link>
          </p>
        </div>
      </div>
    );
  }

  const grad = referrer?.avatarGradient || DEFAULT_GRADIENT;

  return (
    <div className="rs-page">
      <header className="rs-top">
        <Link to="/" className="rs-brand">
          <img src="/assets/dathent-logo.png" alt="" width={28} height={28} />
          <span>Dathent</span>
        </Link>
        <div className="rs-sp" />
        <span className="rs-have">
          {t.have} <Link to="/login">{t.signIn}</Link>
        </span>
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          style={{
            background: 'transparent', color: 'var(--text-muted)',
            border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
            padding: '4px 8px', fontSize: 12, marginLeft: 12,
          }}
        >
          <option value="en">EN</option>
          <option value="uk">UK</option>
          <option value="ru">RU</option>
        </select>
        <div style={{ marginLeft: 8 }}>
          <ThemeToggle />
        </div>
      </header>

      <main className="rs-shell">
        <section className="rs-invite">
          <div className="rs-invite-bg" />
          <div className="rs-invite-inner">
            <span className="rs-eyebrow">
              <span className="rs-eyebrow-dot" />
              {t.eyebrow}
            </span>

            <div className="rs-author">
              {referrer?.photoUrl ? (
                <img
                  className="rs-author-av"
                  src={referrer.photoUrl}
                  alt=""
                  style={{ objectPosition: referrer.photoPosition || '50% 50%' }}
                />
              ) : (
                <div
                  className="rs-author-av"
                  style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}
                >
                  {(referrer?.displayName || 'A').slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="rs-author-text">
                <div className="rs-author-name">{referrer?.displayName || 'Friend'}</div>
                <div className="rs-author-studio">{referrer?.studio || ''}</div>
              </div>
            </div>

            <h1 className="rs-h">
              {referrer?.displayName || 'A friend'} {t.headPre} <span className="ac">{t.headAccent}</span>
            </h1>

            {referrer?.customMessage && (
              <blockquote className="rs-message">
                {referrer.customMessage}
                <span className="rs-message-attr">{referrer.displayName}</span>
              </blockquote>
            )}

            <div className="rs-perks">
              {t.perks.map((p, i) => (
                <div key={i} className="rs-perk">
                  <span className="rs-perk-ic">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      {i === 0 && <path d="M12 3 13.5 8 18 9.5 13.5 11 12 16 10.5 11 6 9.5 10.5 8z" />}
                      {i === 1 && (<><rect x="3" y="4.5" width="18" height="16.5" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></>)}
                      {i === 2 && (<><path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>)}
                    </svg>
                  </span>
                  <div>
                    <div className="rs-perk-l">{p.l}</div>
                    <div className="rs-perk-s">{p.s}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rs-form-wrap">
          <form className="rs-form" onSubmit={submit}>
            <span className="rs-bonus-pill">{t.bonus}</span>

            <div className="rs-form-h">
              <h2>{t.formTitle}</h2>
              <p>{t.formSub}</p>
            </div>

            <div className="rs-field">
              <label className="rs-l">{t.emailL}</label>
              <input
                className="rs-input"
                type="email"
                required
                placeholder={t.emailPh}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div className="rs-field">
              <div className="rs-l-row">
                <label className="rs-l">{t.contactL}</label>
                <span className="rs-l-opt">{t.contactOpt}</span>
              </div>
              <div className="rs-contact">
                <select
                  className="rs-contact-kind"
                  value={contactKind}
                  onChange={(e) => setContactKind(e.target.value)}
                >
                  <option value="phone">{t.contactKindPhone}</option>
                  <option value="telegram">{t.contactKindTelegram}</option>
                  <option value="email">{t.contactKindEmail}</option>
                  <option value="other">{t.contactKindOther}</option>
                </select>
                <input
                  className="rs-input"
                  type="text"
                  placeholder={t.contactPh}
                  value={contactValue}
                  onChange={(e) => setContactValue(e.target.value)}
                  maxLength={200}
                />
              </div>
            </div>

            {error && <div className="rs-error">{error}</div>}

            <button type="submit" className="btn btn-primary rs-cta" disabled={submitting}>
              {submitting ? t.submitting : (<>
                {t.cta}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </>)}
            </button>

            <div className="rs-foot">
              {t.foot[0]}<a href="#">{t.foot[1]}</a>{t.foot[2]}<a href="#">{t.foot[3]}</a>{t.foot[4]}
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

export default ReferralSignup;
